import React, { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// The tab title per route. Each one leads with the page, the way the nav
// names it, so a row of pinned tabs stays readable; the suffix is the brand.
// Home keeps the full positioning line it has in public/index.html. Routes
// not listed here fall back to it, which is also what the catch-all renders.
const HOME_TITLE = "Meira AI · Making AI safe, accountable, and provable";

const TITLES = {
  "/": HOME_TITLE,
  "/platform": "Platform · One control plane for AI trust · Meira AI",
  "/ai-security": "AI Security · Test your AI before someone else does · Meira AI",
  "/governance": "Governance & Standards · ISO 42001, EU AI Act, NIST AI RMF · Meira AI",
  "/assurance": "Assurance & Proof · Proof, not promises · Meira AI",
  "/why-meira": "Why Meira · Verifiable assurance, end to end · Meira AI",
  "/about": "About · Building the trust layer for the AI era · Meira AI",
  "/contact": "Contact · Meira AI",
  "/verify": "Verify · Check a Meira assurance record · Meira AI",
};

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    let raf;
    function loop(time) {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    }
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Lenis drives the page by calling scrollTo itself, which `overflow: hidden`
  // does not stop. So a full-screen overlay has to pause it as well as clamp
  // the document, or a wheel or trackpad gesture still moves the page behind.
  const setScrollLock = useCallback((locked) => {
    const lenis = lenisRef.current;
    if (!lenis) return;
    if (locked) lenis.stop();
    else lenis.start();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Routing is client-side, so the document title is ours to keep in step.
    document.title = TITLES[pathname.replace(/(.)\/+$/, "$1")] ?? HOME_TITLE;
  }, [pathname]);

  return (
    <div className="App relative min-h-screen bg-ink">
      <Navbar onScrollLock={setScrollLock} />
      <main className="relative z-[2]">{children}</main>
      <Footer />
    </div>
  );
}
